import { FormEvent, useEffect, useState, type ReactNode } from "react";
import { Eye, EyeOff, LoaderCircle } from "lucide-react";
import { loginApp } from "./appAuthClient";

const normalizeIdentifier = (value: string) => value.replace(/\s+/g, "").toLowerCase();

export function LoginScreen({ onSuccess }: { onSuccess: () => Promise<void> | void }) {
  const [tenantSlug, setTenantSlug] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "YamarHub App";
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const normalizedTenantSlug = normalizeIdentifier(tenantSlug);
    const normalizedEmail = normalizeIdentifier(email);
    setTenantSlug(normalizedTenantSlug);
    setEmail(normalizedEmail);
    try {
      await loginApp({ tenantSlug: normalizedTenantSlug, email: normalizedEmail, senha });
      await onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível entrar.");
    } finally {
      setLoading(false);
    }
  }

  return <main className="app-lifestyle login-page relative flex min-h-[100dvh] items-center justify-center overflow-hidden px-5 py-4 text-white">
    <div className="wave-field login-wave" aria-hidden="true">{Array.from({ length: 7 }).map((_, i) => <span key={i} />)}</div>

    <section className="relative z-10 w-full max-w-[410px]">
      <div className="login-brand flex items-center justify-center">
        <img src="/brand/logo-app-login.svg" alt="YamarHub App" className="login-app-wordmark h-[74px] w-auto max-w-full" />
      </div>

      <p className="login-intro mx-auto mt-2 max-w-[310px] text-center text-[12px] leading-[1.35rem] text-white/48">
        Acesse sua academia e continue sua jornada.
      </p>

      <form className="login-panel mt-3 space-y-3" onSubmit={submit}>
        <Field label="Academia">
          <input
            required
            autoComplete="organization"
            autoCapitalize="none"
            spellCheck={false}
            value={tenantSlug}
            onChange={e => setTenantSlug(normalizeIdentifier(e.target.value))}
            onBlur={e => setTenantSlug(normalizeIdentifier(e.target.value))}
            className="premium-input"
            placeholder="Digite sua academia"
          />
        </Field>

        <Field label="E-mail">
          <input
            required
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={e => setEmail(normalizeIdentifier(e.target.value))}
            onBlur={e => setEmail(normalizeIdentifier(e.target.value))}
            className="premium-input"
            placeholder="nome@exemplo.com"
          />
        </Field>

        <Field label="Senha">
          <div className="relative">
            <input
              required
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={senha}
              onChange={e => setSenha(e.target.value)}
              className="premium-input pr-12"
              placeholder="Digite sua senha"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-white/42 transition-colors hover:text-teal-light"
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
            </button>
          </div>
        </Field>


        <div className="flex justify-end">
          <button type="button" className="text-xs font-semibold text-teal-light/85 transition-colors hover:text-teal-light">
            Esqueci minha senha
          </button>
        </div>

        {error && <div role="alert" className="rounded-xl border border-red-400/25 bg-red-400/10 px-3.5 py-3 text-sm text-red-100">
          {error}
        </div>}

        <button disabled={loading} className="premium-primary-button w-full">
          {loading
            ? <><LoaderCircle className="h-5 w-5 animate-spin" />Entrando...</>
            : <>Entrar</>}
        </button>
      </form>

      <p className="login-footer mt-3 text-center text-[11px] text-white/38">
        tudo começa pelo movimento
      </p>
    </section>
  </main>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block">
    <span className="mb-2 block text-[12px] font-semibold text-white/68">{label}</span>
    {children}
  </label>;
}
